import { Toaster as Sonner, type ToasterProps } from "sonner";

import { CircularProgress } from "@/components/ui/circular-progress";
import {
  MuiErrorOutlineIcon,
  MuiInfoOutlinedIcon,
  MuiReportProblemOutlinedIcon,
  MuiSuccessOutlinedIcon,
} from "@/components/ui/mui-alert-icons";

// The app's toast outlet, wearing MUI's `Alert variant='standard'` inside
// MUI's `Snackbar` — which is what the donor raises for every result it
// reports (#409, from #254's decision round, D12 = b).
//
// ## Why this restyles sonner rather than rendering `MuiAlert`
//
// Atlas already has both halves of the donor's `Notification` ported —
// `mui-alert.tsx` and `mui-snackbar.tsx` — and composing those would be the
// obvious move. It is the wrong one here: the donor raises its notifications
// *declaratively*, one `<Notification>` per condition per screen, while Atlas
// raises them **imperatively** through `toast.*` from hooks and mutation
// callbacks. Swapping the outlet for a mounted `MuiSnackbar` would mean
// rewriting six call sites across four features to hold their own open state,
// which is a refactor #409 does not ask for and a risk it does not carry. So
// the outlet stays sonner and the *paint* becomes the Alert's, in the one
// shared place, exactly as the ticket scopes it.
//
// ## The parity break that leaves, named
//
// Keeping sonner is a **parity break**, not a free win. The owner ruled it
// kept on #542's decision round (**D2 = a**, 2026-09-22), with no follow-up
// scheduled: the three differences below are accepted, not deferred. The
// paint is the donor's to the computed value; three source-visible
// behaviours are not, and all three are sonner's machinery rather than
// anything this file declares:
//
// 1. **The transition.** The donor's `Snackbar` enters and leaves on MUI's
//    `Grow` — a scale-and-fade from the transform origin, on the theme's
//    `enteringScreen` / `leavingScreen`, 225ms in and 195ms out
//    (`Snackbar.js:113-117,183-204`; `createTransitions.js:36-38`). Sonner
//    slides its toast in from the viewport edge on its own `translateY`
//    keyframes. A user sees the same panel arrive by a different motion.
// 2. **Stacking.** The donor mounts one `Notification` per condition, so two
//    conditions true at once paint two `Snackbar`s at the same anchor, the
//    later one over the earlier. Sonner owns a queue: it stacks toasts with an
//    offset, collapses them behind a front card, and expands on hover. More
//    than one at a time is a different picture on both stacks.
// 3. **Announcement.** MUI's `Alert` defaults to `role='alert'`
//    (`Alert.js:165,195`) — an assertive live region, interrupting a screen
//    reader the moment it mounts. Sonner puts no role on the toast at all and
//    wraps the outlet in a single `aria-live='polite'` section
//    (`sonner@2.0.7`, `dist/index.mjs`), so the same message waits for a
//    pause in whatever is being read. The ink is identical; the announcement
//    is not.
//
// Closing any of the three means the `MuiSnackbar` rewrite above. They stay
// stated here rather than quietly absorbed now that they are ruled: a
// reviewer should be able to find what was traded, and on whose authority,
// without reading sonner's source. Item 3 is the one with real cost — an
// error announced politely can be missed — so a later ticket that revisits
// the outlet has its starting point here.
//
// The donor's placement and duration are already ported, per call site, where
// the donor also varies them: `items-error.ts` (bottom-centre, 32px up, 10s)
// and `items/$itemId.tsx` (top-centre, 136px down, 5s). Those are the donor's
// `sx` margins on the Alert, so the outlet's own offsets below have to be
// MUI's 24px for the totals to land.
//
// ## Everything here is `!important`, and that is not a style choice
//
// Sonner injects its stylesheet into `document.head` at runtime, unlayered,
// with selectors like `[data-sonner-toast][data-styled='true'] [data-title]`
// (specificity 0,3,0). Tailwind's utilities sit in `@layer utilities` at
// (0,1,0) and lose every time — and being injected later, sonner also wins any
// tie. `!` is what makes these declarations land at all. Where two of ours
// could collide — the `body2` weight here and `ItemsError`'s bold — the
// tie is broken by specificity rather than by source order; see the note in
// `items-error.ts`.

/**
 * `AlertRoot`, less the colours: `...typography.body2`, `display: flex`,
 * `padding: '6px 16px'` over a `Paper` at elevation 0 with the theme's 4px
 * corners (`Alert.js:44-60`).
 *
 * Each of these is undoing one of sonner's: 16px of padding, a 1px border, a
 * `0 4px 12px` drop shadow, 8px corners, 13px text, `align-items: center` and
 * a 6px gap. MUI leaves the cross-axis alone — the icon and message slots
 * carry their own vertical padding instead, which is what lines a 22px glyph
 * up with the first line of a message that may have several.
 */
const TOAST_BOX =
  "flex! items-stretch! gap-0! rounded-[4px]! border-0! p-0! px-4! py-1.5! shadow-none! text-sm! leading-[1.43]! font-normal!";

/**
 * The `standard` variant's two colours per severity: ink `darken(light, .6)`,
 * ground `lighten(light, .9)` (`Alert.js:65-72`).
 *
 * Driven off sonner's own `data-type`, so one class string covers every
 * severity and no call site has to pass its own paint.
 *
 * Three, not five. `info` and `loading` keep sonner's neutral panel because
 * neither has a donor to match: `MuiAlert` ports the same three and says so
 * ("the fourth, `info`, has no call site in Atlas yet"), and modsy has no
 * loading toast at all. Deriving an `info` pair from MUI's default palette
 * would be inventing a colour the source never shows.
 */
const TOAST_SEVERITIES = [
  "data-[type=error]:bg-mui-error-surface! data-[type=error]:text-mui-error-ink!",
  "data-[type=success]:bg-mui-success-surface! data-[type=success]:text-mui-success-ink!",
  "data-[type=warning]:bg-mui-warning-surface! data-[type=warning]:text-mui-warning-ink!",
].join(" ");

/**
 * **Content width, without touching sonner's transform.**
 *
 * MUI's `Snackbar` centres itself with `left: 50%; transform:
 * translateX(-50%)` and lets the `Alert` size itself, so a three-word message
 * is a three-word box. Sonner's toast is `position: absolute; left: 0; right:
 * 0` filling a fixed 356px panel.
 *
 * `width: max-content` plus `margin-inline: auto` is what reconciles them: an
 * absolutely positioned box with both `left` and `right` set and a definite
 * width is centred by auto margins, with no `translateX` involved — and the
 * transform has to stay sonner's, because `transform: var(--y)` is the whole
 * enter/exit animation and every stacking offset.
 *
 * **`left-0 right-0` is not redundant.** Sonner states that pair only inside
 * its mobile `@media` block, so above 600px the toast has `left: auto; right:
 * auto` and sits at its static position — the container's left edge. Auto
 * margins have nothing to split there, so both insets have to be definite for
 * the over-constrained margin rule to centre it.
 */
const TOAST_WIDTH = "inset-x-0! mx-auto! w-max! max-w-full!";

/**
 * `AlertIcon`: `marginRight: 12, padding: '7px 0', display: 'flex',
 * fontSize: 22, opacity: 0.9` (`Alert.js:105-112`), and — in the standard
 * variant — painted `palette[severity].main` rather than the ink.
 *
 * Sonner's slot is a 16px square with a `-3px`/`4px` margin pair from its
 * direction variables, and it inherits the toast's colour.
 *
 * The `[&_svg]:mx-0!` is not belt-and-braces: sonner pulls the glyph itself
 * `-1px` left as well, through `--toast-svg-margin-start`, and that one pixel
 * was the entire difference between the donor's 241.23px alert and Atlas's
 * 240.23px when the two were measured side by side.
 */
const TOAST_ICON = [
  "h-auto! w-auto! ml-0! mr-3! py-[7px]! text-[22px]! opacity-90! shrink-0!",
  // `items-start!`, because `AlertIcon` states no `align-items` at all
  // (`Alert.js:105-112`). The root stretches the slot to the message's height
  // and a glyph with a definite 22px height is not stretched, so MUI's sits at
  // the top of it. Sonner centres its slot instead, which is invisible on a
  // one-line toast and plainly wrong on a wrapped one — the glyph floats to
  // the middle of the block rather than sitting beside its first line.
  "items-start!",
  "[&_svg]:mx-0!",
  "group-data-[type=error]/toast:text-lennar-error!",
  "group-data-[type=success]/toast:text-mui-success!",
  "group-data-[type=warning]/toast:text-mui-warning!",
].join(" ");

/** `AlertMessage`, all **three** of it: `padding: '8px 0'`, `minWidth: 0` and
 * `overflow: 'auto'` (`Alert.js:119-125`). The band is what, against the
 * root's 6px, makes a one-line alert 48px tall. Sonner declares none of them,
 * and a `font-weight: 500` this has to undo.
 *
 * The `overflow` is the one of the three with a failure mode, and the easiest
 * to read past: a message carrying an unbreakable token — an id, a URL, a
 * path — scrolls inside the donor's message box, and without it paints
 * outside Atlas's rounded panel. */
const TOAST_MESSAGE = "min-w-0! overflow-auto! py-2! text-sm! leading-[1.43]! font-normal!";

/**
 * The outlet, with the donor's arrangement baked in rather than passed at the
 * mount point.
 *
 * It used to take `position` and `richColors` from `__root.tsx`, which meant
 * every test harness rendering a `<Toaster>` had to remember the same pair and
 * four of them had already drifted from the real one. The outlet is a single
 * shared surface; its configuration belongs with its paint.
 *
 * `richColors` is gone for good: it is sonner's own idea of severity colour —
 * `hsl(360 100% 45%)` on a pink border for an error — and it is precisely what
 * `TOAST_SEVERITIES` replaces.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      // The default shadcn wrapper imports `next-themes` to mirror the app's
      // theme preference. This project doesn't run on Next and has no
      // `next-themes` provider mounted, so we let Sonner's "system" mode follow
      // the OS-level `prefers-color-scheme` directly. Drop the dependency
      // entirely.
      theme="system"
      // Top-centre is the donor's success anchor (`ItemDeleteIsland.tsx:70`);
      // the error call sites ask for `bottom-center` per toast, as the donor
      // does per call site.
      position="top-center"
      // MUI's `Snackbar`: `top/bottom: 24` at `sm` and up, `8` below it
      // (`Snackbar.js:49-70`). Sonner's own defaults are 24 and 16 — the
      // desktop number already matches, the mobile one is off by half, and
      // both are stated here because the call sites' 136px and 32px margins
      // are measured on top of them.
      offset={{ top: 24, bottom: 24, left: 24, right: 24 }}
      mobileOffset={{ top: 8, bottom: 8, left: 8, right: 8 }}
      // **The width ceiling, as a class rather than an inline style, because
      // it has a breakpoint.** `Snackbar` sets no `max-width` at all — the
      // 568px this once carried is `SnackbarContent`'s, and the donor's
      // `Notification` puts an `Alert` straight inside the `Snackbar`
      // (`react-ui/src/notification/notification.tsx:76-99`). The ceiling is
      // a consequence of the positioning instead: above `sm` the root is
      // `position: fixed; left: 50%; right: auto; width: auto`
      // (`Snackbar.js:46-104`), and a fixed shrink-to-fit box with `left` set
      // and `right: auto` is offered *containing block − left* — 50vw. Below
      // `sm` it is `left: 8; right: 8`, so the viewport less 16.
      //
      // `min-[600px]` and not `sm:`, because Tailwind's `sm` is 640px and
      // MUI's is 600 (`createBreakpoints.js`); a 40px band where the two
      // disagree is exactly the width at which this matters.
      //
      // `!` on both, and not because of sonner's injected stylesheet this
      // time: sonner writes `--width` as an *inline* style on the list
      // (`style="--width: 356px"`), which no class beats on the cascade. An
      // `!important` author declaration does. It rides a class rather than
      // the `style` prop below for the only reason that matters here — a
      // breakpoint cannot be expressed in an inline style.
      //
      // `w-[var(--width)]!` is what makes the mobile half of that pair do
      // anything. Above 600px sonner already sizes the list `width:
      // var(--width)`, so the class is a no-op there — but its own mobile
      // `@media` block restates the list as `width: 100%` (`styles.css`,
      // `@media (max-width: 600px)`), and a full-viewport list under an
      // `inset-x-0 mx-auto w-max max-w-full` toast is a toast that can reach
      // the full viewport. Re-pinning the list to `--width` puts the 8px
      // gutters back on both sides, which is the box `left: 8; right: 8`
      // describes.
      className="toaster group w-[var(--width)]! [--width:calc(100vw-16px)]! min-[600px]:[--width:50vw]!"
      icons={{
        // MUI's `defaultIconMapping` (`Alert.js:139-150`) — the same four
        // glyphs the inline `MuiAlert` banner draws, at `1em` against the
        // slot's 22px.
        success: <MuiSuccessOutlinedIcon />,
        // The glyph MUI would draw, on a panel that stays sonner's — see
        // `TOAST_SEVERITIES` on why `info` gets no colours of its own.
        info: <MuiInfoOutlinedIcon />,
        warning: <MuiReportProblemOutlinedIcon />,
        error: <MuiErrorOutlineIcon />,
        // `aria-hidden`, unlike the two spinners on the item card and the
        // detail table. Those two are the donor's, and the donor's bare
        // `CircularProgress` announces its `role="progressbar"` — so they do
        // too. This one has no donor: modsy has no toaster. The toast's own
        // text is already in a live region, and the four icons beside this one
        // are decorative, so the port keeps the `aria-hidden` the `Loader2`
        // here carried rather than adding an announcement the sweep never
        // meant to.
        loading: <CircularProgress size={22} color="inherit" aria-hidden="true" />,
      }}
      toastOptions={{
        classNames: {
          // `group/toast` is what lets the icon slot below read the toast's
          // own `data-type` — sonner stamps the severity on the root, and the
          // severity's `main` colour belongs to the glyph alone.
          toast: `group/toast ${TOAST_BOX} ${TOAST_WIDTH} ${TOAST_SEVERITIES}`,
          icon: TOAST_ICON,
          title: TOAST_MESSAGE,
          description: TOAST_MESSAGE,
        },
      }}
      style={
        {
          // The donor's Inter, not sonner's own system stack — which it sets
          // on the container and would otherwise win by inheritance.
          fontFamily: "var(--font-sans)",
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
